<template>
  <div class="period-selector" data-cy="period-select">
    <div class="period-selector__label text-one-line">{{ t("period") }}:</div>
    <DatePicker
      v-model:start-date="startDate"
      v-model:end-date="endDate"
      v-model:preset="_preset"
      show-preset-buttons
    />
    <UgButton data-cy="apply-period" small :text="t('apply')" @click="applyPeriod" />
  </div>
</template>

<script setup lang="ts">
import { UgButton, DatePicker } from "@bebit/atomic";
import { DateRange, DatePickerPreset } from "@bebit/types";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { isIn30DaysRange } from "@host/utils/date-picker";
import type { Ref } from "vue";

const { t } = useI18n();

const props = defineProps<{
  modelValue: DateRange;
  preset?: DatePickerPreset;
}>();

const emits = defineEmits<{
  (event: "update:modelValue", value: DateRange): void;
  (event: "update:preset", value?: DatePickerPreset): void;
}>();

const startDate: Ref<Date | null> = ref(props.modelValue.start || null);
const endDate: Ref<Date | null> = ref(props.modelValue.end || null);
const _preset: Ref<DatePickerPreset | undefined> = ref(props.preset);

function applyPeriod() {
  const isValidPeriod = startDate.value && endDate.value && isIn30DaysRange(startDate.value, endDate.value);
  if (!isValidPeriod) throw new RangeError(t("errorMessageExceedMaxPeriod"));

  const newPeriod: DateRange = {
    start: startDate.value as Date,
    end: endDate.value as Date,
  };
  emits("update:modelValue", newPeriod);
  emits("update:preset", _preset.value);
}
</script>

<style lang="scss" scoped>
.period-selector {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.period-selector__label {
  color: $colorGray1000;
  font-size: 13px;
  font-weight: 400;
}
</style>

<i18n lang="yaml">
en:
  period: Period
  apply: Apply
  errorMessageExceedMaxPeriod: Please set the period range within one month
ja:
  period: 期間
  apply: 適用
  errorMessageExceedMaxPeriod: 期間の範囲は1ヶ月以内で設定してください
zh-hant:
  period: 期間
  apply: 適用
  errorMessageExceedMaxPeriod: 請將期間範圍設定在一個月以內
zh-hans:
  period: 期间
  apply: 适用
  errorMessageExceedMaxPeriod: 请将期间范围设置在一个月以内
</i18n>
